import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../hooks/AppContext';
import { api } from '../utils/api';
import { fmt, statusBadge, getCurrentPayMonth, monthToNum, getSimulatorMonths, getRentMonth } from '../utils/helpers';

export default function Dashboard() {
  const { activeMonth, isSimulating, switchMonth, isAdmin } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [branchF, setBranchF] = useState('');
  const [acodeF, setAcodeF] = useState('');
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const loadData = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getGodowns(activeMonth)); }
    catch (err) { console.error(err); }
    setLoading(false);
  }, [activeMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading || !data) return <div className="wrap"><div className="loading">Loading...</div></div>;

  const { totalCount, confirmedCount, declinedCount, pendingCount, expiredCount, flaggedCount } = data;
  const rows = data.data;

  // Totals
  let totalNet = 0, confNet = 0;
  rows.forEach(r => { totalNet += Number(r.net_amt); if (r.status === 'yes') confNet += Number(r.net_amt); });

  // Filters
  const branches = [...new Set(rows.map(r => r.cbsbrcd))].sort();
  const acodes = [...new Set(rows.map(r => r.acode))].sort();

  let filtered = rows;
  if (filter !== 'all') filtered = filtered.filter(r => r.status === filter || (filter === 'flagged' && r.confirmation?.issues?.length > 0));
  if (branchF) filtered = filtered.filter(r => r.cbsbrcd === branchF);
  if (acodeF) filtered = filtered.filter(r => r.acode === acodeF);
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(r =>
      r.godown_name?.toLowerCase().includes(s) || r.vendor_name?.toLowerCase().includes(s) ||
      r.pan_no?.toLowerCase().includes(s) || String(r.godown_id).includes(s)
    );
  }

  // Sort
  if (sortCol) {
    filtered.sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const handleReset = async (r) => {
    if (!window.confirm(`Reset confirmation for ${r.godown_name}?`)) return;
    try {
      await api.resetConfirmation(r.godown_id, r.vendor_id, data.month);
      loadData();
    } catch (err) { alert(err.message); }
  };

  const statCards = [
    { label: 'Total', count: totalCount, cls: '', filter: 'all' },
    { label: 'Confirmed', count: confirmedCount, cls: 'stat-yes', filter: 'yes' },
    { label: 'Declined', count: declinedCount, cls: 'stat-no', filter: 'no' },
    { label: 'Pending', count: pendingCount, cls: 'stat-pending', filter: 'pending' },
    { label: 'Expired', count: expiredCount, cls: 'stat-expired', filter: 'expired' },
    { label: 'Flagged', count: flaggedCount, cls: 'stat-flagged', filter: 'flagged' },
  ];

  const SortTh = ({ col, label }) => (
    <th onClick={() => handleSort(col)} style={{cursor:'pointer'}}>
      {label} {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
    </th>
  );

  return (
    <div className="wrap">
      <div className="page-header">
        <h2>Dashboard</h2>
        <p className="sub">
          Showing: <strong style={{color: isSimulating ? 'var(--acc)' : 'var(--pri)'}}>{data.month}</strong>
          {isSimulating ? ' (simulated)' : ''}
          &nbsp;· Net: ₹{fmt(totalNet)} · Confirmed: <span className="text-green">₹{fmt(confNet)}</span>
        </p>
      </div>

      {/* Cutoff inline editor (admin only) */}
      {isAdmin && data.cutoff && (
        <div className="cutoff-inline">
          <span>⏰ CUT-OFF</span>
          <span className="fw-600">{data.month}</span>
          <span>{new Date(data.cutoff.datetime).toLocaleString('en-IN')}</span>
          <span className={data.cutoff.isExpired ? 'text-red' : 'text-green'}>
            {data.cutoff.isExpired ? 'Passed' : 'Active'}
          </span>
        </div>
      )}

      {/* Stat cards */}
      <div className="stat-grid">
        {statCards.map(s => (
          <div key={s.filter} className={`stat-card ${s.cls} ${filter === s.filter ? 'active' : ''}`}
            onClick={() => setFilter(s.filter)}>
            <div className="stat-count">{s.count}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="fbar">
        <select value={branchF} onChange={e => setBranchF(e.target.value)}>
          <option value="">All Branches</option>
          {branches.map(b => <option key={b}>{b}</option>)}
        </select>
        <select value={acodeF} onChange={e => setAcodeF(e.target.value)}>
          <option value="">All Acodes</option>
          {acodes.map(a => <option key={a}>{a}</option>)}
        </select>
        <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        {(branchF || acodeF || search) && (
          <button className="clr-all" onClick={() => { setBranchF(''); setAcodeF(''); setSearch(''); }}>✕ Clear</button>
        )}
      </div>

      <p className="text-sm text-muted">Click column headers to sort · {filtered.length} records</p>

      {/* Table */}
      <div className="tbl-wrap">
        <table>
          <thead><tr>
            <SortTh col="cbsbrcd" label="Branch" />
            <SortTh col="acode" label="Acode" />
            <SortTh col="godown_name" label="Godown" />
            <SortTh col="vendor_name" label="Owner" />
            <SortTh col="monthly_rent" label="Rent" />
            <th className="text-right">TDS</th>
            <th className="text-right">GST</th>
            <th className="text-right">M.DED</th>
            <SortTh col="net_amt" label="Net" />
            <th>Rent Type</th>
            <th>Valid</th>
            <th>Confirmed</th>
            <th>Issues</th>
            {isAdmin && <th>Action</th>}
          </tr></thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 14 : 13} className="text-center text-muted">No records match filters</td></tr>
            ) : filtered.map(r => {
              const gstTotal = (Number(r.sgst)||0) + (Number(r.cgst)||0) + (Number(r.igst)||0) + (Number(r.utgst)||0);
              const issues = r.confirmation?.issues || [];
              return (
                <tr key={`${r.godown_id}_${r.vendor_id}`} className={r.status === 'yes' ? 'row-yes' : r.status === 'expired' ? 'row-expired' : ''}>
                  <td><strong>{r.cbsbrcd}</strong></td>
                  <td>{r.acode}</td>
                  <td>
                    <div className="fw-500">{r.godown_name}</div>
                    <div className="text-xs text-muted">#{r.godown_id}</div>
                  </td>
                  <td>
                    <div>{r.vendor_name}</div>
                    <div className="text-xs text-muted">V:{r.vendor_id}</div>
                  </td>
                  <td className="text-right">₹{fmt(r.monthly_rent)}</td>
                  <td className="text-right" style={{color: r.tds > 0 ? 'var(--red)' : 'var(--gry)'}}>
                    {r.tds > 0 ? `₹${fmt(r.tds)}` : '—'}
                  </td>
                  <td className="text-right" style={{color: gstTotal > 0 ? 'var(--amb)' : 'var(--gry)'}}>
                    {gstTotal > 0 ? `₹${fmt(gstTotal)}` : '—'}
                  </td>
                  <td className="text-right" style={{color: r.mded > 0 ? 'var(--red)' : 'var(--gry)'}}>
                    {r.mded > 0 ? `₹${fmt(r.mded)}` : '—'}
                  </td>
                  <td className="text-right fw-600">₹{fmt(r.net_amt)}</td>
                  <td>
                    <div className="fw-700" style={{color: 'var(--pri)'}}>{getRentMonth(data.month, r.pay_nature)}</div>
                    <span className={r.pay_nature === 'Adv' ? 'type-adv' : 'type-cur'}>
                      {r.pay_nature === 'Adv' ? 'Prepaid' : 'Postpaid'}
                    </span>
                  </td>
                  <td>{statusBadge(r.status)}</td>
                  <td>
                    {r.confirmation?.confirmedAt ? (
                      <div>
                        <div className="fw-700 text-sm">{new Date(r.confirmation.confirmedAt).toLocaleDateString('en-IN')}</div>
                        <div className="text-xs text-muted">{new Date(r.confirmation.confirmedAt).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</div>
                      </div>
                    ) : '—'}
                  </td>
                  <td>{issues.length > 0 ? issues.map(i => <span key={i} className="issue-tag">{i}</span>) : '—'}</td>
                  {isAdmin && (
                    <td>
                      {r.status !== 'pending' && (
                        <button className="btn btn-d btn-sm" onClick={() => handleReset(r)}>Reset</button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
